import { Link, useLocation } from "react-router-dom";
import { LOGO_URL } from "@/lib/api";
import { SERVICES } from "@/data/services";
import { LOCAL_PAGES } from "@/data/local";
import { SERVICE_AREAS } from "@/lib/seo";

const Footer = () => {
  const { pathname } = useLocation();
  if (pathname === "/admin") return null;

  return (
    <footer className="bg-[#0A0A0A] border-t border-white/5 pt-24 pb-12 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
          <div className="md:col-span-4">
            <img src={LOGO_URL} alt="Ember & Steel Studio" className="h-24 w-24 object-contain mb-6" />
            <p className="text-zinc-500 text-sm leading-relaxed max-w-sm">
              Laser engraving &amp; UV printing studio. Every piece is cut, burned or printed
              in-house on the xTool F2 Ultra and our UV flatbed — one at a time, never rushed.
            </p>
            <p className="text-zinc-600 text-xs leading-relaxed max-w-sm mt-4" data-testid="footer-service-area">
              Custom laser engraving, personalised drinkware, corporate gifts and UV printing from Leduc, Alberta —
              serving {SERVICE_AREAS.slice(0, -1).join(", ")} and {SERVICE_AREAS.at(-1)}. Shipping across Canada and to the USA.
            </p>
          </div>
          <div className="md:col-span-3">
            <h4 className="font-mono-tech text-xs uppercase tracking-[0.25em] text-[#C87455] mb-6">Services</h4>
            <ul className="space-y-3">
              {SERVICES.map((s) => (
                <li key={s.slug}><Link to={`/services/${s.slug}`} className="text-zinc-400 hover:text-white transition-colors text-sm" data-testid={`footer-service-${s.slug}`}>{s.nav}</Link></li>
              ))}
              <li><Link to="/faq" className="text-zinc-400 hover:text-white transition-colors text-sm" data-testid="footer-link-faq">FAQ</Link></li>
            </ul>
          </div>
          <div className="md:col-span-2">
            <h4 className="font-mono-tech text-xs uppercase tracking-[0.25em] text-[#C87455] mb-6">Navigate</h4>
            <ul className="space-y-3">
              <li><Link to="/shop" className="text-zinc-400 hover:text-white transition-colors text-sm" data-testid="footer-link-shop">Shop</Link></li>
              <li><Link to="/gallery" className="text-zinc-400 hover:text-white transition-colors text-sm" data-testid="footer-link-gallery">Gallery</Link></li>
              <li><Link to="/portfolio" className="text-zinc-400 hover:text-white transition-colors text-sm" data-testid="footer-link-portfolio">Case Studies</Link></li>
              <li><Link to="/quote" className="text-zinc-400 hover:text-white transition-colors text-sm" data-testid="footer-link-quote">Custom Work</Link></li>
              <li><Link to="/business" className="text-zinc-400 hover:text-white transition-colors text-sm" data-testid="footer-link-business">For Business</Link></li>
              <li><Link to="/track" className="text-zinc-400 hover:text-white transition-colors text-sm" data-testid="footer-link-track">Track Order</Link></li>
              <li><Link to="/gift-cards" className="text-zinc-400 hover:text-white transition-colors text-sm" data-testid="footer-link-giftcards">Gift Cards</Link></li>
              <li><Link to="/admin" className="text-zinc-600 hover:text-zinc-400 transition-colors text-sm" data-testid="footer-link-admin">Studio Login</Link></li>
            </ul>
          </div>
          <div className="md:col-span-3">
            <h4 className="font-mono-tech text-xs uppercase tracking-[0.25em] text-[#C87455] mb-6">The Workshop</h4>
            <ul className="space-y-3 text-sm text-zinc-400 font-mono-tech">
              <li>Leduc, Alberta, Canada</li>
              <li><a href="mailto:sales@embersteelstudio.ca" className="hover:text-[#C87455] transition-colors" data-testid="footer-email-link">sales@embersteelstudio.ca</a></li>
              <li><a href="tel:+17806033400" className="hover:text-[#C87455] transition-colors" data-testid="footer-phone-link">(780) 603-3400</a></li>
              <li className="flex gap-4 pt-2 text-zinc-500">
                <span className="hover:text-[#C87455] cursor-pointer transition-colors">Instagram</span>
                <span className="hover:text-[#C87455] cursor-pointer transition-colors">Facebook</span>
                <span className="hover:text-[#C87455] cursor-pointer transition-colors">TikTok</span>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/5 pt-10 mb-20" data-testid="footer-service-areas">
          <h4 className="font-mono-tech text-xs uppercase tracking-[0.25em] text-[#C87455] mb-6">Service Areas</h4>
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-3">
            {LOCAL_PAGES.map((p) => (
              <li key={p.slug}><Link to={`/${p.slug}`} className="text-zinc-400 hover:text-white transition-colors text-sm" data-testid={`footer-local-${p.slug}`}>{p.nav}</Link></li>
            ))}
          </ul>
        </div>
      </div>

      <div className="select-none pointer-events-none px-2">
        <h2 className="font-serif-display font-light text-center whitespace-nowrap text-[13vw] leading-none tracking-tight text-transparent bg-clip-text bg-gradient-to-b from-zinc-800 to-transparent">
          EMBER &amp; STEEL
        </h2>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-8 flex flex-col sm:flex-row justify-between gap-4 border-t border-white/5 pt-8">
        <p className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-600">
          © {new Date().getFullYear()} Ember &amp; Steel Studio — All marks are permanent
        </p>
        <p className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-600">
          Laser Engraving • UV Printing • Prices in CAD
        </p>
      </div>
    </footer>
  );
};

export default Footer;
